import type { EntitySuggestion, StepHelpers } from '@bliks-io/contracts';
import { HelpersPicker } from '@/components/entity-pickers/helpers/HelpersPicker';
import { useEditorSuggestionSource } from './useEditorSuggestionSource';

/** The stored helpers slot, flattened: the editor defaults every bucket to an
 *  empty array before handing it to the picker. */
export type EditorHelpers = Required<StepHelpers>;

interface Props {
  helpers: EditorHelpers;
  processId?: string;
  /** Draft node the suggestions raised here anchor to (`stableStepId`). */
  recordingId?: string;
  /** Pending suggestions on the source node, for ghost rows + pre-checking. */
  entitySuggestions?: EntitySuggestion[];
  onChange: (next: EditorHelpers) => void;
}

/**
 * Editor adapter around the shared {@link HelpersPicker}: feeds it the editor's
 * flattened helpers value and emits the **full** helpers object on each change
 * (the draft update replaces `helpers` wholesale).
 */
export function EditorHelpersSlot({
  helpers,
  processId,
  recordingId,
  entitySuggestions,
  onChange
}: Props) {
  const { persons, orgFunctions, systems, documents } = helpers;
  const { anchorId, allowCreate } = useEditorSuggestionSource(recordingId);
  return (
    <HelpersPicker
      persons={persons}
      orgFunctions={orgFunctions}
      systems={systems}
      documents={documents}
      onHelpersChange={(next) =>
        onChange({
          persons: next.persons,
          orgFunctions: next.orgFunctions,
          systems,
          documents
        })
      }
      onSystemsChange={(next) => onChange({ persons, orgFunctions, systems: next, documents })}
      onDocumentsChange={(next) => onChange({ persons, orgFunctions, systems, documents: next })}
      processId={processId}
      recordingId={anchorId}
      draftProcessId={processId}
      allowCreate={allowCreate}
      entitySuggestions={entitySuggestions}
      stacked
    />
  );
}
